import { useState } from 'react';

import {
  Alert,
  Box,
  Button,
  ConfirmationDialog,
  ExperienceCard,
  ExperienceCardSettingsTile,
  ExperienceCardsSection,
  Icon,
} from '@liscio/ui';

import type { DashboardAction, DashboardActionId } from './dashboardActions';

export interface WhatToDoTodayWidgetProps {
  actions: DashboardAction[];
  visibleActions: DashboardAction[];
  isLoadingPreferences: boolean;
  onVisibilityChange: (
    actionId: DashboardActionId,
    visible: boolean
  ) => Promise<boolean>;
  savingActionId: DashboardActionId | null;
  preferenceError: string | null;
  onClearPreferenceError: () => void;
}

export function WhatToDoTodayWidget({
  actions,
  visibleActions,
  isLoadingPreferences,
  onVisibilityChange,
  savingActionId,
  preferenceError,
  onClearPreferenceError,
}: WhatToDoTodayWidgetProps) {
  const [customizing, setCustomizing] = useState(false);
  const [pendingRemoval, setPendingRemoval] =
    useState<DashboardAction | null>(null);
  const visibleIds = new Set(visibleActions.map((action) => action.id));
  const isSaving = savingActionId !== null;
  const showOnlyCustomize =
    visibleActions.length === 0 && !isLoadingPreferences && !customizing;

  const confirmRemoval = async () => {
    if (!pendingRemoval) return;

    const target = pendingRemoval;
    const saved = await onVisibilityChange(target.id, false);
    if (saved) {
      setPendingRemoval((current) =>
        current?.id === target.id ? null : current
      );
    }
  };

  const headerAction = (
    <Button
      variant={customizing ? 'contained' : 'outlined'}
      startIcon={customizing ? undefined : <Icon icon="sliders" />}
      disabled={isLoadingPreferences}
      onClick={() => setCustomizing((current) => !current)}
    >
      {customizing ? 'Done' : 'Customize'}
    </Button>
  );

  const settingsContent = customizing
    ? {
        heading: 'Customizing your home',
        description:
          "Add workflows back to your home, or remove the ones you don't need.",
        label: 'Workflows',
        children: actions.map((action) => {
          const visible = visibleIds.has(action.id);
          return (
            <ExperienceCardSettingsTile
              key={action.id}
              title={action.title}
              description={action.description}
              icon={action.icon}
              statusLabel="Dismissed"
              ctaLabel={action.ctaLabel}
              activateAriaLabel={`Try ${action.title}`}
              actionLabel={
                savingActionId === action.id
                  ? 'Saving...'
                  : visible
                    ? 'Remove from home'
                    : 'Add to home'
              }
              actionAriaLabel={
                visible
                  ? `Remove ${action.title}`
                  : `Add ${action.title} to dashboard`
              }
              dismissAriaLabel={`Dismiss ${action.title} from home`}
              visible={visible}
              disabled={isSaving}
              onActivate={action.onActivate}
              onAction={() => {
                if (visible) {
                  setPendingRemoval(action);
                } else {
                  void onVisibilityChange(action.id, true);
                }
              }}
            />
          );
        }),
      }
    : undefined;

  return (
    <>
      {showOnlyCustomize ? (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            {headerAction}
          </Box>
          {preferenceError && (
            <Alert
              severity="error"
              onClose={onClearPreferenceError}
              sx={{ mt: 2 }}
            >
              {preferenceError}
            </Alert>
          )}
        </Box>
      ) : (
        <ExperienceCardsSection
          heading="What do you want to do today?"
          description="Explore a Liscio workflow or share it with a teammate."
          headerAction={headerAction}
          errorMessage={preferenceError}
          onClearError={onClearPreferenceError}
          isLoading={isLoadingPreferences}
          loadingRegionLabel="Loading experience cards"
          loadingItems={actions.map((action) => ({
            key: action.id,
            label: `Loading ${action.title}`,
          }))}
          settingsContent={settingsContent}
          isEmpty={visibleActions.length === 0}
          emptyMessage="All experience cards are hidden."
          emptyActionLabel="Customize dashboard"
          emptyActionIcon="sliders"
          onEmptyAction={() => setCustomizing(true)}
        >
          {visibleActions.map((action) => (
            <ExperienceCard
              key={action.id}
              title={action.title}
              description={action.description}
              ctaLabel={action.ctaLabel}
              activateLabel={`Try ${action.title}`}
              removeLabel={`Remove ${action.title} from dashboard`}
              icon={action.icon}
              disabled={isSaving}
              onActivate={action.onActivate}
              onRemove={() => setPendingRemoval(action)}
            />
          ))}
        </ExperienceCardsSection>
      )}

      <ConfirmationDialog
        open={pendingRemoval !== null}
        title={`Remove ${pendingRemoval?.title ?? 'experience'}?`}
        content={
          preferenceError ??
          'This experience will be removed from your dashboard. You can add it back anytime from Customize.'
        }
        confirmLabel="Remove"
        confirmColor="error"
        disabled={isSaving}
        isLoading={isSaving}
        onConfirm={() => void confirmRemoval()}
        onCancel={() => setPendingRemoval(null)}
      />
    </>
  );
}
